import { useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import { getArticleById, getRelatedArticles, getHotArticles } from "@/mocks/articles";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  const article = getArticleById(id || "1");
  const relatedArticles = article ? getRelatedArticles(article) : [];
  const hotArticles = getHotArticles().slice(0, 5);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-article-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-900 mb-2">文章不存在</h2>
            <p className="text-foreground-500 mb-6">该文章可能已被删除或链接失效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      "@type": "Organization",
      name: "51吃瓜官网",
      url: "https://51chigua.com",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://51chigua.com/article/${article.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 51吃瓜官网`}
        description={article.excerpt.slice(0, 150)}
        keywords={article.tags.join(",")}
        canonicalUrl={`https://51chigua.com/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
        schemaData={[articleSchema]}
      />

      <Navbar />

      <main className="pt-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />
        </div>

        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pb-10">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Main Content */}
            <article className="lg:w-8/12">
              {/* Article Header */}
              <header className="mb-8">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-200 transition-colors"
                  >
                    {article.category}
                  </Link>
                  {article.isHot && (
                    <span className="px-2 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full flex items-center gap-1">
                      <i className="ri-fire-line text-xs"></i>热门
                    </span>
                  )}
                  {article.isNew && (
                    <span className="px-2 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full flex items-center gap-1">
                      <i className="ri-flashlight-line text-xs"></i>最新
                    </span>
                  )}
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-snug">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>{article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>{article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>{(article.views / 10000).toFixed(1)}万阅读
                  </span>
                </div>
              </header>

              {/* Cover Image */}
              <div className="w-full h-[300px] md:h-[420px] rounded-xl overflow-hidden mb-8">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.title} - 51吃瓜官网`}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Article Content */}
              <div
                className="article-content max-w-none"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="mt-10 pt-6 border-t border-background-200/70">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 bg-background-100 text-sm text-foreground-600 rounded-full border border-background-200/70 cursor-pointer hover:border-primary-300 hover:text-primary-600 transition-colors"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <section className="mt-10">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                    <h3 className="font-heading text-lg font-bold text-foreground-900">相关推荐</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {relatedArticles.map((rel) => (
                      <ArticleCard key={rel.id} article={rel} />
                    ))}
                  </div>
                </section>
              )}
            </article>

            {/* Sidebar */}
            <aside className="lg:w-4/12">
              <div className="bg-background-100 rounded-xl p-5 border border-background-200/70 sticky top-24">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4 flex items-center gap-2">
                  <i className="ri-fire-line text-primary-500"></i>热门爆料
                </h3>
                <div className="flex flex-col gap-0">
                  {hotArticles.map((hot, index) => (
                    <Link
                      key={hot.id}
                      to={`/article/${hot.id}`}
                      className={`group flex items-start gap-3 py-3 border-b border-background-200/70 last:border-0 cursor-pointer ${
                        hot.id === article.id ? "opacity-50 pointer-events-none" : ""
                      }`}
                    >
                      <span className="w-6 h-6 flex items-center justify-center rounded bg-primary-100 text-primary-600 text-xs font-bold flex-shrink-0 mt-0.5">
                        {index + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                          {hot.title}
                        </p>
                        <span className="text-xs text-foreground-400 mt-1 block">{hot.publishDate}</span>
                      </div>
                    </Link>
                  ))}
                </div>

                <div className="mt-6 pt-5 border-t border-background-200/70">
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">关于作者</h3>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-user-line text-primary-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                      <p className="text-xs text-foreground-500">51吃瓜爆料团队</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    专注娱乐八卦爆料，为您带来第一手劲爆资讯。
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}